import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Edit3, Trash2, PowerOff, Power, Loader2, Eye, Download, X, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { GlassCard, PageHeader } from "../dashboard/Common";
import ExcelUploadBox from "./ExcelUploadBox";
import FieldRenderer from "./FieldRenderer";
import {
  listResources, getResource, createResource, updateResource, deleteResource, toggleStatus,
  bulkCreate, bulkDelete, fmtDate, downloadCSV,
} from "../../lib/resources";
import { subscribeDataChanged } from "../../lib/dataEvents";

const PAGE_SIZE = 25;
const SENTINEL_ID = "rs-sentinel";

export default function ResourceManager(props) {
  const {
    kind, title, subtitle, eyebrow,
    fields = [], columns, transformBeforeSave,
    excelHint, excelEnabled = true,
    onDataChanged, defaultForm, tableTestId,
    basePath, readOnly = false, downloadable = false, selectable = false,
    filters = [], viewable = false,
    addLabel = "Add new",
  } = props;

  const location = useLocation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(() => { try { return new URLSearchParams(location.search).get("q") || ""; } catch { return ""; } });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm || {});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadCount = useRef(0);
  const onUploading = useRef((v) => {
    uploadCount.current += v ? 1 : -1;
    setUploading(uploadCount.current > 0);
  }).current;
  const [toDelete, setToDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const qRef = useRef(q);
  qRef.current = q;

  const { dynamicFields, onFormChange } = props;
  const resolvedFields = useMemo(() => {
    return dynamicFields ? dynamicFields(fields, form) : fields;
  }, [fields, form, dynamicFields]);

  const prevFormRef = useRef(form);
  useEffect(() => {
    if (prevFormRef.current !== form) {
      onFormChange?.(form, setForm, prevFormRef.current);
      prevFormRef.current = form;
    }
  }, [form, onFormChange]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const loadInitial = useCallback(async (searchQ) => {
    setLoading(true);
    setSelected(new Set());
    pageRef.current = 1;
    loadingMoreRef.current = false;
    let ok = false;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const data = await listResources(kind, searchQ, basePath, 1, PAGE_SIZE);
        const fetched = data.items || [];
        setItems(fetched);
        setTotal(data.total || 0);
        hasMoreRef.current = fetched.length < (data.total || 0);
        onDataChanged && onDataChanged(fetched);
        ok = true; break;
      } catch {
        if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 1000));
      }
    }
    if (!ok) toast.error("Failed to load records");
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, basePath]);

  // Silent refresh: fetches fresh data WITHOUT showing skeleton loaders.
  // Used after mutations so the table stays visible with optimistic data while
  // the background fetch quietly updates the list.
  const silentRefresh = useCallback(async () => {
    try {
      const data = await listResources(kind, qRef.current, basePath, 1, PAGE_SIZE);
      const fetched = data.items || [];
      setItems(fetched);
      setTotal(data.total || 0);
      hasMoreRef.current = fetched.length < (data.total || 0);
      pageRef.current = 1;
      onDataChanged && onDataChanged(fetched);
    } catch { /* optimistic data stays visible */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, basePath]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const data = await listResources(kind, qRef.current, basePath, nextPage, PAGE_SIZE);
      const newItems = data.items || [];
      setItems((prev) => {
        const merged = [...prev, ...newItems];
        hasMoreRef.current = merged.length < (data.total || 0);
        return merged;
      });
      pageRef.current = nextPage;
    } catch {
      toast.error("Failed to load more records");
    }
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }, [kind, basePath]);

  // Initial load on kind/basePath change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInitial(q); }, [kind, basePath]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadInitial(q), 280);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const el = document.getElementById(SENTINEL_ID);
    if (!el || !hasMoreRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, items]);

  // Keep the table in sync with changes made anywhere (other tabs, imports, etc.).
  // Uses debounced silentRefresh so multiple rapid events (e.g. bulk ops) don't
  // cause duplicate API calls, and the table stays visible (no skeleton flash).
  useEffect(() => {
    let timer;
    const unsub = subscribeDataChanged((ev) => {
      if (ev.kind && ev.kind !== kind) return;
      clearTimeout(timer);
      timer = setTimeout(() => silentRefresh(), 300);
    });
    return () => { unsub(); clearTimeout(timer); };
  }, [kind, silentRefresh]);

  const filtered = useMemo(() => {
    try {
      if (!filters.length) return items || [];
      return (items || []).filter((row) =>
        filters.every((f) => {
          const v = filterValues[f.key];
          if (!v || v === "__all__") return true;
          const cell = row?.[f.key];
          if (Array.isArray(cell)) return cell.includes(v);
          return String(cell ?? "") === String(v);
        })
      );
    } catch { return items || []; }
  }, [items, filters, filterValues]);

  const sorted = useMemo(() => {
    try {
      if (!sortKey || !sortDir) return filtered;
      return [...filtered].sort((a, b) => {
        const aVal = a?.[sortKey];
        const bVal = b?.[sortKey];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        let cmp = 0;
        if (typeof aVal === "number" && typeof bVal === "number") {
          cmp = aVal - bVal;
        } else if (aVal instanceof Date && bVal instanceof Date) {
          cmp = aVal.getTime() - bVal.getTime();
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        }
        return sortDir === "asc" ? cmp : -cmp;
      });
    } catch { return filtered; }
  }, [filtered, sortKey, sortDir]);

  const safeSorted = sorted || [];
  const allSelected = safeSorted.length > 0 && safeSorted.every((r) => r?.id && selected.has(r.id));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(safeSorted.map((r) => r.id).filter(Boolean)));

  const openAdd = () => { setEditing(null); setForm(defaultForm || {}); setOpen(true); };
  const openEdit = async (row) => {
    setEditing(row);
    setForm({ ...row });
    setOpen(true);
    try {
      const full = await getResource(kind, row.id, basePath);
      setForm((prev) => ({ ...prev, ...(full || {}) }));
    } catch { /* keep row data */ }
  };
  const openView = async (row) => {
    setViewing(row);
    try {
      const full = await getResource(kind, row.id, basePath);
      setViewing((prev) => (prev && prev.id === row.id ? (full || prev) : prev));
    } catch { /* keep row data */ }
  };
  const closeDialog = () => { setOpen(false); uploadCount.current = 0; setUploading(false); };

  const onSave = async (e) => {
    e?.preventDefault?.();
    if (uploading) { toast.error("Please wait for image upload to finish."); return; }
    if (props.onBeforeSave?.(form, resolvedFields) === false) return;
    for (const f of resolvedFields) {
      if (f.required && (form[f.key] === undefined || form[f.key] === "" || (Array.isArray(form[f.key]) && form[f.key].length === 0))) {
        toast.error(`${f.label} is required`); return;
      }
      if (f.type === "password" && typeof form[f.key] === "string" && form[f.key].length > 0 && form[f.key].length < (f.minLength || 6)) {
        toast.error(`${f.label} must be at least ${f.minLength || 6} characters`); return;
      }
    }
    if (!editing) {
      for (const f of resolvedFields) {
        if (f.type === "password" && f.minLength && (!form[f.key] || form[f.key].length < f.minLength)) {
          toast.error(`${f.label} is required (min ${f.minLength} characters)`); return;
        }
      }
    }
    setSaving(true);
    let prevRow = null;
    try {
      const fieldMap = Object.fromEntries(resolvedFields.map((f) => [f.key, f]));
      const body = transformBeforeSave ? transformBeforeSave(form, fieldMap) : form;
      if (editing) {
        prevRow = items.find((r) => r.id === editing.id);
        if (prevRow) setItems((prev) => prev.map((r) => r.id === editing.id ? { ...r, ...body } : r));
        await updateResource(kind, editing.id, body, basePath);
        toast.success("Updated");
      }
      else {
        const result = await createResource(kind, body, basePath);
        toast.success("Saved");
        if (result?.item) {
          setItems((prev) => [result.item, ...prev]);
          setTotal((prev) => (prev || 0) + 1);
        }
      }
      closeDialog();
      // No need to call loadInitial — broadcast() in resources.js fires
      // notifyDataChanged which triggers silentRefresh via subscribeDataChanged.
    } catch (e) {
      if (prevRow) setItems((prev) => prev.map((r) => r.id === editing?.id ? prevRow : r));
      toast.error(e.response?.data?.detail || "Save failed");
    }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const deletedId = toDelete.id;
    try {
      await deleteResource(kind, deletedId, basePath);
      toast.success("Deleted");
      setToDelete(null);
      setItems((prev) => prev.filter((r) => r.id !== deletedId));
      setTotal((prev) => Math.max(0, (prev || 0) - 1));
      // broadcast() triggers silentRefresh via subscribeDataChanged
    } catch { toast.error("Delete failed"); }
  };
  const onToggle = async (row) => {
    const next = row.status === "disabled" ? "active" : "disabled";
    try {
      await toggleStatus(kind, row.id, next, basePath);
      toast.success(`Marked ${next}`);
      setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      // broadcast() triggers silentRefresh via subscribeDataChanged
    } catch { toast.error("Update failed"); }
  };
  const onBulkRows = async (rows) => {
    // Map of lowercased keys and labels to field.key
    const keyMap = {};
    (resolvedFields || []).forEach((f) => {
      if (f?.key) {
        const k = String(f.key).toLowerCase().trim();
        keyMap[k] = f.key;
        keyMap[k.replace(/[^a-z0-9]/g, "")] = f.key;
      }
      if (f?.label) {
        const l = String(f.label).toLowerCase().trim();
        keyMap[l] = f.key;
        keyMap[l.replace(/[^a-z0-9]/g, "")] = f.key;
      }
    });

    const ALIASES = {
      name: ["name", "student name", "student_name", "student", "full name", "full_name", "school name", "school_name", "class name", "class_name", "course name", "course_name", "subject name", "subject_name", "chapter name", "chapter_name", "title"],
      student_id: ["student id", "student_id", "student code", "student_code", "roll no", "roll_no", "roll number", "roll_number", "id"],
      school_name: ["school name", "school_name", "school", "school code", "school_code"],
      class_name: ["class name", "class_name", "class", "grade", "standard", "std"],
      course_name: ["course name", "course_name", "course", "courses", "course_names"],
      subject_name: ["subject name", "subject_name", "subject"],
      chapter_name: ["chapter name", "chapter_name", "chapter"],
      parent_name: ["parent name", "parent_name", "parent", "father name", "father_name", "mother name", "mother_name", "guardian"],
      phone: ["phone", "phone_number", "phone number", "mobile", "mobile_number", "mobile number", "contact", "contact_number"],
      email: ["email", "email_address", "email address", "mail"],
      division: ["division", "div", "section"],
      address: ["address", "location"],
      principal_name: ["principal name", "principal_name", "principal"],
      code: ["code", "school code", "school_code"],
      password: ["password", "pass"],
      description: ["description", "desc", "details"],
      price: ["price", "amount", "cost"],
      stock: ["stock", "quantity", "qty"],
      category: ["category", "cat"],
      sku: ["sku", "product_sku", "item_code"],
      video_url: ["video url", "video_url", "video"],
      url: ["url", "link", "website"],
      class_names: ["class_names", "class names", "classes"],
      course_names: ["course_names", "course names", "courses"],
    };

    const normalised = rows
      .map((raw) => {
        if (!raw || typeof raw !== "object") return null;
        const clean = {};
        let hasVal = false;

        Object.entries(raw).forEach(([k, v]) => {
          if (!k || String(k).startsWith("__")) return;
          const origKey = String(k).trim();
          if (!origKey || /^_\d+$/.test(origKey)) return;
          const lowerKey = origKey.toLowerCase();
          const strippedKey = lowerKey.replace(/[^a-z0-9]/g, "");

          // 1. Direct match with keyMap
          let targetKey = keyMap[lowerKey] || keyMap[strippedKey];

          // 2. Alias matching
          if (!targetKey) {
            for (const [canonical, aliasList] of Object.entries(ALIASES)) {
              if (aliasList.some((alias) => alias.replace(/[^a-z0-9]/g, "") === strippedKey)) {
                targetKey = canonical;
                break;
              }
            }
          }

          // 3. Fallback to snake_case of lowerKey
          if (!targetKey) {
            targetKey = lowerKey.replace(/\s+/g, "_");
          }

          let cleanVal = v;
          if (cleanVal === null || cleanVal === undefined || cleanVal === "NaN" || cleanVal === "null" || cleanVal === "undefined") {
            cleanVal = "";
          } else if (typeof cleanVal === "string") {
            cleanVal = cleanVal.trim();
          }

          if (cleanVal !== "") hasVal = true;

          const fDef = (resolvedFields || []).find((f) => f.key === targetKey);
          if (fDef && fDef.type === "multi-select" && typeof cleanVal === "string") {
            cleanVal = cleanVal.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
          } else if (["class_names", "course_names"].includes(targetKey) && typeof cleanVal === "string") {
            cleanVal = cleanVal.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
          }

          clean[targetKey] = cleanVal;
        });

        return hasVal ? clean : null;
      })
      .filter(Boolean);

    if (!normalised.length) {
      toast.error("No valid data rows found to upload.");
      return;
    }

    try {
      const result = await bulkCreate(kind, normalised, basePath);
      const inserted = result?.inserted ?? 0;
      const failed = result?.failed ?? 0;
      const errors = result?.errors || [];

      if (failed > 0) {
        let msg = `Partial Success: Uploaded ${inserted} record(s). ${failed} record(s) failed.`;
        if (errors.length > 0) {
           msg += ` Errors: ` + errors.map(e => `[Row ${e.row || '?'}: ${e.message}]`).join(" ");
        }
        toast.error(msg, { duration: 8000 });
      } else {
        toast.success(`Successfully uploaded ${inserted} record(s).`);
      }
      
      // broadcast() triggers silentRefresh via subscribeDataChanged
    } catch (e) { 
      toast.error(e.response?.data?.detail || e.message || "Bulk upload failed"); 
    }
  };
  const onBulkDelete = async () => {
    if (!selected.size) return;
    const deletedIds = new Set(selected);
    try {
      await bulkDelete(kind, Array.from(deletedIds), basePath);
      toast.success(`Deleted ${deletedIds.size}`);
      setSelected(new Set());
      setItems((prev) => prev.filter((r) => r.id && !deletedIds.has(r.id)));
      setTotal((prev) => Math.max(0, (prev || 0) - deletedIds.size));
      // broadcast() triggers silentRefresh via subscribeDataChanged
    } catch { toast.error("Bulk delete failed"); }
  };
  const onDownload = () => {
    if (!filtered.length) { toast("Nothing to download"); return; }
    downloadCSV(`${kind}-${Date.now()}.csv`, filtered, columns);
    toast.success("Downloading CSV");
  };

  const showSkeleton = loading && items.length === 0;
  const skeletonRows = 6;

  const renderActions = (row) => (
    <div className="flex gap-1.5 flex-wrap">
      {viewable && (
        <Button size="sm" variant="outline" onClick={() => openView(row)} className="h-8 bg-white/5 border-white/10 text-cyan-200 hover:bg-cyan-400/10" data-testid={`${kind}-view-${row.id}`}>
          <Eye size={12} className="mr-1" />View
        </Button>
      )}
      {props.extraActions && props.extraActions(row, setItems)}
      {!readOnly && (
        <>
          <Button size="sm" variant="outline" onClick={() => openEdit(row)} className="h-8 bg-white/5 border-white/10 text-cyan-200 hover:bg-cyan-400/10" data-testid={`${kind}-edit-${row.id}`}>
            <Edit3 size={12} className="mr-1" />Edit
          </Button>
          {props.customActions ? props.customActions(row, setItems) : (
            <Button size="sm" variant="outline" onClick={() => onToggle(row)} className="h-8 bg-white/5 border-white/10 text-amber-200 hover:bg-amber-400/10" data-testid={`${kind}-disable-${row.id}`}>
              {row.status === "disabled" ? <><Power size={12} className="mr-1" />Enable</> : <><PowerOff size={12} className="mr-1" />Disable</>}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setToDelete(row)} className="h-8 bg-white/5 border-white/10 text-red-300 hover:bg-red-500/15" data-testid={`${kind}-delete-${row.id}`}>
            <Trash2 size={12} className="mr-1" />Delete
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div data-testid={`${kind}-page`}>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle}
        action={!readOnly ? (
          <Button onClick={openAdd} className="bg-gradient-to-r from-[#00E5FF] to-[#0055FF] text-white shadow-[0_8px_28px_rgba(0,229,255,0.35)]" data-testid={`${kind}-add-button`}>
            <Plus size={16} className="mr-2" /> {addLabel}
          </Button>
        ) : null}
      />

      {excelEnabled && !readOnly && (
        <div className="mb-5"><ExcelUploadBox onRows={onBulkRows} hint={excelHint} testid={`${kind}-excel`} /></div>
      )}

      <GlassCard>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]" data-testid={`${kind}-search-input`} />
          </div>
          {filters.map((f) => (
            <Select key={f.key} value={filterValues[f.key] || "__all__"} onValueChange={(v) => setFilterValues((s) => ({ ...s, [f.key]: v }))}>
              <SelectTrigger className="h-10 w-full sm:w-40 bg-white/5 border-white/10 text-white" data-testid={`${kind}-filter-${f.key}`}>
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] border-white/10 text-white">
                <SelectItem value="__all__">All {f.label}</SelectItem>
                {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <div className="flex items-center gap-2 flex-wrap md:ml-auto">
            {downloadable && (
              <Button variant="outline" onClick={onDownload} className="h-10 bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid={`${kind}-download`}>
                <Download size={14} className="mr-2" /> Download
              </Button>
            )}
            {selectable && !readOnly && selected.size > 0 && (
              <Button variant="outline" onClick={onBulkDelete} className="h-10 bg-red-500/10 border-red-400/40 text-red-200 hover:bg-red-500/20" data-testid={`${kind}-bulk-delete`}>
                <Trash2 size={14} className="mr-2" /> Delete ({selected.size})
              </Button>
            )}
            {loading && !items.length && <Loader2 size={16} className="animate-spin text-cyan-300" />}
            <span className="text-xs text-[#64748B]">{total} record{total===1?"":"s"}</span>
            <Button
              onClick={() => window.location.reload()}
              className="h-10 w-10 bg-white/5 border border-white/10 text-white hover:bg-white/10"
              data-testid={`${kind}-refresh-button`}
              title="Refresh page"
            >
              <RefreshCw size={15} />
            </Button>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid={tableTestId || `${kind}-table`}>
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#64748B]">
                {selectable && !readOnly && (
                  <th className="px-3 py-2 font-medium w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} className="border-white/20 data-[state=checked]:bg-cyan-400" data-testid={`${kind}-select-all`} />
                  </th>
                )}
                <th className="px-3 py-2 font-medium">Sr</th>
                {columns.map((c) => (
                  <th key={c.key} className="px-3 py-2 font-medium cursor-pointer hover:text-white select-none" onClick={() => handleSort(c.key)}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sortKey === c.key ? (
                        sortDir === "asc" ? <ArrowUp size={12} className="text-cyan-400" /> : <ArrowDown size={12} className="text-cyan-400" />
                      ) : (
                        <ArrowUpDown size={12} className="opacity-30" />
                      )}
                    </span>
                  </th>
                ))}
                {!readOnly || viewable || props.extraActions ? <th className="px-3 py-2 font-medium text-right">Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                Array.from({ length: skeletonRows }).map((_, i) => (
                  <tr key={`skel-${i}`} className="border-t border-white/5">
                    {selectable && !readOnly && <td className="px-3 py-3"><div className="w-4 h-4 rounded bg-white/5 animate-pulse" /></td>}
                    <td className="px-3 py-3"><div className="w-6 h-3 rounded bg-white/5 animate-pulse" /></td>
                    {columns.map((c) => (
                      <td key={c.key} className="px-3 py-3"><div className="h-3 rounded bg-white/5 animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                    ))}
                    {(!readOnly || viewable || props.extraActions) && <td className="px-3 py-3"><div className="w-24 h-6 rounded bg-white/5 animate-pulse ml-auto" /></td>}
                  </tr>
                ))
              ) : items.length === 0 && !loading ? (
                <tr><td colSpan={columns.length + 2 + (selectable && !readOnly ? 1 : 0)} className="px-3 py-10 text-center text-[#64748B]">
                  No records {readOnly ? "yet — ask an admin to add some." : <>yet. Click <span className="text-cyan-300">{addLabel}</span> or upload via Excel.</>}
                </td></tr>
              ) : (
                safeSorted.map((row, i) => (
                  <motion.tr key={row.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.01 }}
                    data-testid={`${kind}-row-${row.id}`}
                    className={`border-t border-white/5 hover:bg-white/3 ${row.status === "disabled" ? "opacity-60" : ""}`}>
                    {selectable && !readOnly && (
                      <td className="px-3 py-3">
                        <Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleSel(row.id)} className="border-white/20 data-[state=checked]:bg-cyan-400" data-testid={`${kind}-select-${row.id}`} />
                      </td>
                    )}
                    <td className="px-3 py-3 text-[#A0ABC0] font-mono">{i + 1}</td>
                    {columns.map((c) => (
                      <td key={c.key} className="px-3 py-3 text-white/90">{c.render ? c.render(row, i) : row[c.key] ?? "—"}</td>
                    ))}
                    {(!readOnly || viewable || props.extraActions) && (
                      <td className="px-3 py-3">
                        {renderActions(row)}
                      </td>
                    )}
                  </motion.tr>
                ))
              )}
              {loadingMore && (
                <tr>
                  <td colSpan={columns.length + 2 + (selectable && !readOnly ? 1 : 0)} className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-[#64748B] text-xs">
                      <Loader2 size={14} className="animate-spin text-cyan-400" />
                      Loading more…
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden space-y-3">
          {showSkeleton ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <div key={`m-skel-${i}`} className="rounded-xl border border-white/5 bg-white/3 p-4 space-y-2 animate-pulse">
                <div className="h-4 rounded bg-white/10 w-1/2" />
                <div className="h-3 rounded bg-white/5 w-3/4" />
              </div>
            ))
          ) : items.length === 0 && !loading ? (
            <div className="rounded-xl border border-white/5 bg-white/3 p-8 text-center text-[#64748B]">
              No records {readOnly ? "yet — ask an admin to add some." : <>yet. Click <span className="text-cyan-300">{addLabel}</span> or upload via Excel.</>}
            </div>
          ) : (
            safeSorted.map((row, i) => (
              <motion.div
                key={row.id}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.01 }}
                data-testid={`${kind}-mcard-${row.id}`}
                className={`rounded-xl border border-white/5 bg-white/3 p-4 ${row.status === "disabled" ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {selectable && !readOnly && (
                    <Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleSel(row.id)} className="border-white/20 data-[state=checked]:bg-cyan-400 mt-1" data-testid={`${kind}-select-${row.id}`} />
                  )}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {columns.map((c, ci) => (
                      <div key={c.key} className="flex items-start gap-2 text-sm">
                        <span className="text-[11px] uppercase tracking-widest text-[#64748B] w-24 shrink-0 pt-0.5">
                          {ci === 0 ? `${i + 1}. ` : ""}{c.label}
                        </span>
                        <span className="text-white/90 break-words flex-1 min-w-0">{c.render ? c.render(row, i) : row[c.key] ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {(!readOnly || viewable || props.extraActions) && (
                  <div className="mt-3 pt-3 border-t border-white/5">{renderActions(row)}</div>
                )}
              </motion.div>
            ))
          )}
          {loadingMore && (
            <div className="py-3 flex items-center justify-center gap-2 text-[#64748B] text-xs">
              <Loader2 size={14} className="animate-spin text-cyan-400" /> Loading more…
            </div>
          )}
        </div>

        {/* Sentinal element for IntersectionObserver */}
        {hasMoreRef.current && items.length > 0 && (
          <div id={SENTINEL_ID} className="h-4" />
        )}

        {total > PAGE_SIZE && items.length > 0 && (
          <div className="flex items-center justify-between pt-3 text-xs text-[#64748B] border-t border-white/5 mt-3">
            <span>Loaded {items.length} of {total}</span>
            {!hasMoreRef.current && <span className="text-emerald-400">All records loaded</span>}
          </div>
        )}
      </GlassCard>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="bg-[#0B1120] border border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Edit" : "Add new"} {title}</DialogTitle>
            <DialogDescription className="text-[#A0ABC0]">Fill the form and click Save changes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2" data-testid={`${kind}-form`}>
            {resolvedFields.map((f) => (
              <FieldRenderer key={f.key} field={f} value={form[f.key]} onChange={(val) => setForm((s) => ({ ...s, [f.key]: val }))} onUploadingChange={onUploading} kind={kind} />
            ))}
            <DialogFooter className="md:col-span-2 pt-2">
              <Button type="button" variant="outline" onClick={closeDialog} className="bg-white/5 border-white/10 text-white" data-testid={`${kind}-cancel-button`}>Cancel</Button>
              <Button type="submit" disabled={saving || uploading} className="bg-gradient-to-r from-[#00E5FF] to-[#0055FF] text-white" data-testid={`${kind}-save-button`}>
                {saving ? <><Loader2 size={14} className="animate-spin mr-2" />Saving…</> : uploading ? <><Loader2 size={14} className="animate-spin mr-2" />Uploading…</> : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[#0B1120] border border-white/10 text-white max-w-xl" data-testid={`${kind}-view-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{viewing?.name || viewing?.student_name || viewing?.title || "Details"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {viewing && Object.entries(viewing).filter(([k]) => !["_id", "password", "amount_paise"].includes(k)).map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-[#64748B]">{k}</p>
                <div className="text-white break-all mt-1">
                  {v !== null && typeof v === "object" && !Array.isArray(v) ? (
                    <div className="space-y-1 text-xs">
                      {Object.entries(v).map(([subK, subV]) => (
                        <div key={subK}><span className="text-[#94A3B8]">{subK}:</span> {String(subV ?? "—")}</div>
                      ))}
                    </div>
                  ) : Array.isArray(v) ? (
                    v.join(", ")
                  ) : (
                    String(v ?? "—")
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0B1120] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">This will permanently remove <span className="text-white">{toDelete?.name || toDelete?.student_name || toDelete?.id}</span>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid={`${kind}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid={`${kind}-delete-confirm`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { fmtDate };
export function useResourceList(kind, basePath) {
  const [items, setItems] = useState([]);
  const mountedRef = useRef(true);
  const reload = useCallback(async () => {
    try {
      const data = await listResources(kind, "", basePath, 1, 5000);
      if (mountedRef.current) setItems(data.items || []);
    } catch {
      if (mountedRef.current) setItems([]);
    }
  }, [kind, basePath]);

  useEffect(() => {
    mountedRef.current = true;
    reload();
    return () => { mountedRef.current = false; };
  }, [reload]);

  // Auto-refresh when data of this kind changes (create/update/delete/toggle/upload/import).
  // Debounced to avoid duplicate API calls from rapid successive events.
  useEffect(() => {
    let timer;
    const unsub = subscribeDataChanged((ev) => {
      if (ev.kind && ev.kind !== kind) return;
      clearTimeout(timer);
      timer = setTimeout(() => reload(), 300);
    });
    return () => { unsub(); clearTimeout(timer); };
  }, [kind, reload]);

  return [items, reload];
}
